export default class UserNavigation extends HTMLElement {
	static #selectors = {
		backdrop: "#user-profile-menu-backdrop",
		closeButton: "#user-profile-menu-close-button",
		userNavigation: "#user-navigation",
		userNavigationAvatar: ".UserNavigation-avatar img",
		userNavigationFlyoutAvatar: ".CardUserProfile-avatar img",
		userNavigationMain: "#user-navigation-menu-main",
		userNavigationNav: "#user-navigation-nav",
		userNavigationTrigger: "#user-navigation-trigger",
	};

	#elements;

	// eslint-disable-next-line jsdoc/require-jsdoc
	#getElements() {
		return {
			backdrop: /** @type {HTMLDivElement} */ (
				this.querySelector(UserNavigation.#selectors.backdrop)
			),
			userNavigation: /** @type {HTMLElement} */ (
				this.querySelector(UserNavigation.#selectors.userNavigation)
			),
			userNavigationAvatar: /** @type {HTMLImageElement} */ (
				this.querySelector(UserNavigation.#selectors.userNavigationAvatar)
			),
			userNavigationFlyoutAvatar: /** @type {HTMLImageElement} */ (
				this.querySelector(UserNavigation.#selectors.userNavigationFlyoutAvatar)
			),
			userNavigationMain: /** @type {HTMLElement} */ (
				document.querySelector(UserNavigation.#selectors.userNavigationMain)
			),
			userNavigationNav: /** @type {HTMLUListElement} */ (
				this.querySelector(UserNavigation.#selectors.userNavigationNav)
			),
			closeButton: /** @type {HTMLButtonElement} */ (
				this.querySelector(UserNavigation.#selectors.closeButton)
			),
			userNavigationTrigger: /** @type {HTMLButtonElement} */ (
				this.querySelector(UserNavigation.#selectors.userNavigationTrigger)
			),
		};
	}

	/**
	 * Initializes the UserNavigation constructor.
	 *
	 * This constructor creates an instance of the UserNavigation class and initializes
	 * the necessary elements for the user profile menu. It sets the 'hidden' attribute to
	 * 'true' for the user profile menu element and adds event listeners for the necessary
	 * elements.
	 */
	constructor() {
		super();

		this.#elements = this.#getElements();

		if (this.#elements.userNavigationTrigger && this.#elements.userNavigation) {
			this.#elements.userNavigation.setAttribute("hidden", "");
			this.#addEventListeners();
		}
	}

	/**
	 * Show or hide the user profile menu based on the value of the "shouldShow" parameter.
	 * @param {boolean} shouldShow - Indicates whether the element should be shown or hidden.
	 */
	#toggleUserNavigation(shouldShow) {
		const {
			backdrop,
			closeButton,
			userNavigation,
			userNavigationMain,
			userNavigationTrigger,
		} = this.#elements;
		userNavigationTrigger.setAttribute("aria-expanded", String(shouldShow));

		if (shouldShow) {
			userNavigation.removeAttribute("hidden");

			// If the close button is visible, focus on it. Otherwise, focus on the
			// navigation landmark.
			if (closeButton.checkVisibility()) {
				closeButton.focus();
			} else {
				userNavigationMain.focus();
			}

			document.documentElement.style.overflow = "hidden";
		} else {
			userNavigation.setAttribute("hidden", "");
			backdrop.setAttribute("hidden", "");

			userNavigationTrigger.focus();

			document.documentElement.style.overflow = "";
		}
	}

	/**
	 * Adds event listeners to the user profile menu.
	 * @returns {void}
	 */
	#addEventListeners() {
		this.#elements.userNavigationTrigger.addEventListener("click", () => {
			const shouldShow = this.#elements.userNavigation.hasAttribute("hidden");
			this.#toggleUserNavigation(shouldShow);
		});

		this.#elements.closeButton.addEventListener("click", () => {
			this.#toggleUserNavigation(false);
		});

		this.addEventListener("UpdateAvatar", (event) => {
			const { userNavigationAvatar, userNavigationFlyoutAvatar } =
				this.#elements;
			if (userNavigationAvatar) {
				userNavigationAvatar.src = event.detail.avatarBase64;
			}

			if (userNavigationFlyoutAvatar) {
				userNavigationFlyoutAvatar.src = event.detail.avatarBase64;
			}
		});

		document.addEventListener("keyup", (event) => {
			if (
				!this.#elements.userNavigation.hasAttribute("hidden") &&
				event.key === "Escape"
			) {
				this.#toggleUserNavigation(false);
			}
		});

		document.addEventListener("click", (event) => {
			const isClickInsideUserNavigation =
				this.#elements.userNavigation.contains(event.target) ||
				this.#elements.userNavigationTrigger.contains(event.target);

			if (
				!isClickInsideUserNavigation &&
				!this.#elements.userNavigation.hasAttribute("hidden")
			) {
				this.#toggleUserNavigation(false);
			}
		});
	}
}

customElements.define("user-navigation", UserNavigation);
